// MCHD Room Scheduler - Floor Form Component
// Form for creating and editing floors

import React, { useEffect } from 'react';
import { Modal, NumberInput, TextInput, Switch, Button, Group, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';

interface Floor {
  id: string;
  number: number;
  name: string;
  buildingId: string;
  isActive: boolean;
}

interface FloorFormProps {
  floor?: Floor | null;
  buildingId: string;
  onSubmit: (floorData: any) => void;
  onClose: () => void;
}

/**
 * Floor Form Component
 * Handles creating and editing floors
 */
export const FloorForm: React.FC<FloorFormProps> = ({
  floor,
  buildingId,
  onSubmit,
  onClose
}) => {
  const isEditing = !!floor;

  const form = useForm({
    initialValues: {
      number: 1,
      name: '',
      isActive: true
    },
    validate: {
      number: (value) => {
        if (!value) return 'Floor number is required';
        if (value < 1) return 'Floor number must be at least 1';
        if (value > 999) return 'Floor number cannot exceed 999';
        return null;
      },
      name: (value) => {
        if (!value) return 'Floor name is required';
        if (value.length < 2) return 'Floor name must be at least 2 characters';
        if (value.length > 100) return 'Floor name cannot exceed 100 characters';
        return null;
      }
    }
  });

  /**
   * Initialize form with floor data when editing
   */
  useEffect(() => {
    if (floor) {
      form.setValues({
        number: floor.number,
        name: floor.name,
        isActive: floor.isActive
      });
    }
  }, [floor]);

  /**
   * Handle form submission
   */
  const handleSubmit = (values: typeof form.values) => {
    const submitData = {
      ...values,
      buildingId
    };
    
    onSubmit(submitData);
  };

  /**
   * Handle form reset
   */
  const handleReset = () => {
    form.reset();
  };

  return (
    <Modal
      opened={true}
      onClose={onClose}
      title={isEditing ? 'Edit Floor' : 'Create New Floor'}
      size="md"
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* Floor Number Field */}
          <NumberInput
            label="Floor Number"
            placeholder="Enter floor number"
            min={1}
            max={999}
            required
            {...form.getInputProps('number')}
          />

          {/* Floor Name Field */}
          <TextInput
            label="Floor Name"
            placeholder="Enter floor name (e.g., Ground Floor, First Floor)"
            required
            {...form.getInputProps('name')}
          />

          {/* Status Field (only for editing) */}
          {isEditing && (
            <Switch
              label="Active Status"
              description="Whether this floor is active and available for use"
              {...form.getInputProps('isActive', { type: 'checkbox' })}
            />
          )}

          {/* Form Actions */}
          <Group justify="flex-end" mt="xl">
            <Button
              variant="outline"
              onClick={handleReset}
            >
              Reset
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={form.values.name === ''} // Simple loading state
            >
              {isEditing ? 'Update Floor' : 'Create Floor'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
